import { defineStore } from "pinia";
import { onMounted, onUnmounted, ref } from "vue";
import { api } from "@lr-notitia/notitia-dictionary/client";
import type { Symbol as DictionarySymbol, Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import type { LanguageCode } from "iso-639-1";
import { useDomainStore } from "./domain";

let domainStore: ReturnType<typeof useDomainStore>;
function init() {
	domainStore = useDomainStore();
}

export const useSymbolsStore = defineStore("symbols", () => {
	const symbols = ref<Record<string, DictionarySymbol>>({});
	const dictionaryColor = ref("#9575cd");

	async function loadSymbols() {
		return new Promise<void>((resolve, reject) => {
			api()
				.symbols()
				.get()
				.then((data) => {
					data.forEach((symbol) => {
						symbols.value[symbol.id] = symbol;
					});
					resolve();
				})
				.catch((err) => {
					console.error("LOAD SYMBOLS ERROR", err);
					reject(err);
				});
		});
	}
	function byId(id: string) {
		return symbols.value[id];
	}

	// id = id for the global dictionary symbol if it exists (refersTo) - jnhck
	function term(data: { id?: string; terminology?: Terminology; lang?: string }): string {
		if (!domainStore) init();
		if (!data.id && !data.terminology) return "";
		const language = data.lang || domainStore.languagePreference;
		const symbol = data.id ? byId(data.id) : null;
		if (data.terminology?.[language]?.term?.value) return data.terminology[language].term.value;
		if (symbol?.terminology[language]?.term?.value) return symbol.terminology[language].term.value;
		if (data.terminology?.en?.term?.value) return data.terminology.en.term.value;
		if (symbol?.terminology.en?.term?.value) return symbol.terminology.en.term.value;
		const localLanguages = Object.keys(data.terminology || {});
		if (localLanguages.length > 0) return data.terminology?.[localLanguages[0]]?.term?.value || "";
		const symbolLanguages = Object.keys(symbol?.terminology || {});
		if (symbolLanguages.length > 0) return symbol?.terminology[symbolLanguages[0]]?.term?.value || "";
		return data.id || "";
	}
	function description(data: { id?: string; terminology?: Terminology; lang?: string }): string {
		if (!domainStore) init();
		if (!data.id && !data.terminology) return "";
		const language = data.lang || domainStore.languagePreference;
		const symbol = data.id ? byId(data.id) : null;
		if (data.terminology?.[language]?.description?.value) return data.terminology[language].description.value;
		if (symbol?.terminology[language]?.description?.value) return symbol.terminology[language].description.value;
		if (data.terminology?.en?.description?.value) return data.terminology.en.description.value;
		if (symbol?.terminology.en?.description?.value) return symbol.terminology.en.description.value;
		const localLanguages = Object.keys(data.terminology || {});
		if (localLanguages.length > 0) return data.terminology?.[localLanguages[0]]?.description?.value || "";
		const symbolLanguages = Object.keys(symbol?.terminology || {});
		if (symbolLanguages.length > 0) return symbol?.terminology[symbolLanguages[0]]?.description?.value || "";
		return "";
	}
	function synonyms(data: { id?: string; terminology?: Terminology; lang?: string }) {
		if (!domainStore) init();
		if (!data.id && !data.terminology) return [];
		const language = data.lang || domainStore.languagePreference;
		const symbol = data.id ? byId(data.id) : null;
		if (data.terminology?.[language]?.synonyms) return data.terminology[language].synonyms.map((s) => s.value);
		if (symbol?.terminology[language]?.synonyms) return symbol.terminology[language].synonyms.map((s) => s.value);
		return [];
	}

	function GUILabel(symbolId: string, fields?: Record<string, string>, lang?: string): string {
		if (!domainStore) init();
		const symbol = byId(symbolId);
		if (!symbol) return "NO GUI SYMBOL FOUND";
		if (!symbol.context?.tags?.includes("NOTITIA_GUI")) return "SYMBOL IS NOT A GUI SYMBOL";
		if (!lang) lang = domainStore.languagePreference;
		const terminology = symbol.terminology[lang];
		if (!terminology) return "NO GUI TERMINOLOGY FOUND";
		let term = terminology.term?.value;
		if (!term) return "NO GUI TERM FOUND";
		for (const field in fields) {
			term = term.replace(new RegExp(`{{ ${field} }}`, "g"), fields[field]);
		}
		term = term.trim();
		return term;
	}

	function isImmutable(id: string): boolean {
		return immutableSymbolIds.includes(id);
	}
	// TODO: ein mal alle checken ob noch irgendwo benutzt werden (Engineer, Designer, Operator), wenn nicht entfernen
	const immutableSymbolIds = [
		"00000000-0000-0000-0000-000000000100",
		"00000000-0000-0000-0001-000000000000",
		"00000000-0000-0000-0000-000000000101",
		"00000000-0000-0000-0000-000000000102",
		"00000000-0000-0000-0000-000000000103",
		"00000000-0000-0000-0000-000000000104",
		"00000000-0000-0000-0000-000000000105",
		"00000000-0000-0000-0000-000000000106",
		"00000000-0000-0000-0000-000000000107",
		"00000000-0000-0000-0000-000000000108",
		"logic_gate_and_symbol",
		"condition_task_symbol",
		"question_task_symbol",
		"logic_gate_not_symbol",
		"logic_gate_or_symbol",
		"00000000-0000-0000-0000-000000000109",
		"system_task_symbol",
		"logic_gate_xor_symbol",
	];

	const languages = ref<LanguageCode[]>([
		"en",
		"de",
		"fr",
		"es",
		"it",
		"pl",
		"nl",
		"da",
		"fi",
		"sv",
		"no",
		"pt",
		"el",
		"ro",
		"cs",
		"hu",
		"ko",
		"zh",
		"id",
		"tr",
		"uk",
		"ru",
		"ar",
	]);

	function search(query: string, lang?: LanguageCode): DictionarySymbol[] {
		const language = lang || domainStore.languagePreference;
		return Object.values(symbols.value).filter((symbol) => {
			return (
				term({ id: symbol.id, lang: language }).toLowerCase().includes(query.toLowerCase()) ||
				synonyms({ id: symbol.id, lang: language }).some((synonym) =>
					synonym.toLowerCase().includes(query.toLowerCase())
				) ||
				term({ id: symbol.id, lang: "en" }).toLowerCase().includes(query.toLowerCase()) ||
				synonyms({ id: symbol.id, lang: "en" }).some((synonym) =>
					synonym.toLowerCase().includes(query.toLowerCase())
				)
			);
		});
	}

	onMounted(() => {
		api()
			.socket()
			.symbols()
			.on((message) => {
				switch (message.method) {
					case "create":
						symbols.value[message.data.id] = message.data;
						break;
					case "update":
						symbols.value[message.data.id] = message.data;
						break;
					case "delete":
						delete symbols.value[message.data];
						break;
					default:
						break;
				}
			});
	});

	onUnmounted(() => {
		api().socket().symbols().off();
	});

	return {
		loadSymbols,
		symbols,
		byId,
		term,
		description,
		synonyms,
		isImmutable,
		dictionaryColor,
		search,
		languages,
		GUILabel,
		immutableSymbolIds,
	};
});
